import axios, {AxiosResponse} from 'axios';
import {
  IPatientAssignmentsResponse,
  IPatientAssistantDialogueResponse,
  IPatientAssistantSessionResponse,
  IPatientCareTeamResponse,
  IPatientLibraryResponse,
  IPatientMedicationsResponse,
  IMedicationAdherenceResponse,
  IMedicationCheckinsResponse,
  IPatientRunResponse,
  IProgramAllowedManualEventsResponse,
  IProgramCareTeamMember,
  IProgramDetail,
  IProgramLibraryItem,
  IProgramModule,
  IProgramNext,
  IStepCompleteResponse,
  IStepContent,
  IStepDraft,
  MedicationMarkStatus,
  StepKind
} from '../intervention/interfaces';

// Patient Home ("Interventions") read endpoints — UCSF-3138.
// REACT_APP_API_URL already includes the `/api/v3` prefix; auth headers
// (Authorization + API_KEY) are attached globally via setAxiosHeaders.
//
// Profile is intentionally NOT defined here: the Home page reuses the existing
// `/user/profile` call (getUserProfileApi in redux/api/user.ts), not the `/me` alias.

export const getPatientAssignmentsApi = (): Promise<AxiosResponse<IPatientAssignmentsResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/assignments`);
};

export const getPatientRunApi = (apId: number): Promise<AxiosResponse<IPatientRunResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}`);
};

export const getPatientLibraryApi = (apId: number): Promise<AxiosResponse<IPatientLibraryResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/library/${apId}`);
};

export const getPatientMedicationsApi = (): Promise<AxiosResponse<IPatientMedicationsResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/medications`);
};

export const getPatientCareTeamApi = (): Promise<AxiosResponse<IPatientCareTeamResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/care-team`);
};

export const getPatientAssistantSessionApi = (): Promise<AxiosResponse<IPatientAssistantSessionResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/assistant/session`);
};

export const getPatientAssistantDialogueApi = (): Promise<AxiosResponse<IPatientAssistantDialogueResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/assistant/dialogue`);
};

// Program detail endpoints (UCSF-3145). Sub-paths under /api/v3/patient/run/{apId}/
export const getProgramDetailApi = (apId: number): Promise<AxiosResponse<IProgramDetail>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}`);
};

export const getProgramNextApi = (apId: number): Promise<AxiosResponse<IProgramNext | null>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/next`);
};

export const getProgramModulesApi = (apId: number): Promise<AxiosResponse<IProgramModule[]>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/modules`);
};

export const getProgramLibraryApi = (apId: number): Promise<AxiosResponse<IProgramLibraryItem[]>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/library`);
};

export const getProgramCareTeamApi = (apId: number): Promise<AxiosResponse<IProgramCareTeamMember[]>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/care_team`);
};

// Patient self-service lifecycle actions (UCSF-3546): rescreen/pause/resume/reactivate.
export const getAllowedManualEventsApi = (apId: number): Promise<AxiosResponse<IProgramAllowedManualEventsResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/allowed-manual-events`);
};

export const postManualEventApi = (
  apId: number, eventCode: string, reason?: string
): Promise<AxiosResponse<unknown>> => {
  return axios.post(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/manual-event`, {eventCode, reason});
};

// Step content endpoints (UCSF-3168). Sub-paths under /api/v3/patient/run/{apId}/step/{stepId}/
//
// UCSF-3496: getStep/saveDraft/complete accept an optional `stepKind` query param ("pas" | "sip"),
// needed to open a Recurring/Multi-instance module's item (a step_item_progress_id, not a PAS id).
// getDraft/deleteDraft deliberately do NOT take it — bot.step_draft is keyed by a bare step_id
// column, agnostic to which id-space it came from (see PatientRunStepRestController).
export const getStepContentApi = (
  apId: number, stepId: number, stepKind?: StepKind
): Promise<AxiosResponse<IStepContent>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/step/${stepId}`, {
    params: stepKind ? {stepKind} : undefined
  });
};

export const completeStepApi = (
  apId: number,
  stepId: number,
  body: {contentId: number; payload: Record<string, unknown>; clientTs: string},
  stepKind?: StepKind
): Promise<AxiosResponse<IStepCompleteResponse>> => {
  return axios.post(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/step/${stepId}/complete`, body, {
    params: stepKind ? {stepKind} : undefined
  });
};

export const saveStepDraftApi = (
  apId: number,
  stepId: number,
  body: {contentId: number; payload: Record<string, unknown>},
  stepKind?: StepKind
): Promise<AxiosResponse<{updatedAt: string}>> => {
  return axios.post(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/step/${stepId}/draft`, body, {
    params: stepKind ? {stepKind} : undefined
  });
};

export const getStepDraftApi = (apId: number, stepId: number): Promise<AxiosResponse<IStepDraft | null>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/run/${apId}/step/${stepId}/draft`);
};

// Medication check-in (UCSF-3579 backend, UCSF-3078 page). A dose that is no longer pending returns 409.
export const getMedicationCheckinsApi = (): Promise<AxiosResponse<IMedicationCheckinsResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/medication-checkin/all`);
};

export const markMedicationDoseApi = (logId: number, status: MedicationMarkStatus): Promise<AxiosResponse> => {
  return axios.post(`${process.env.REACT_APP_API_URL}/patient/medication-checkin/dose/${logId}/mark`, {status});
};

// Adherence over the last 7 days for the patient's tracked medications (UCSF-3614 backend, UCSF-3615 page).
export const getMedicationAdherenceApi = (): Promise<AxiosResponse<IMedicationAdherenceResponse>> => {
  return axios.get(`${process.env.REACT_APP_API_URL}/patient/medication-adherence`);
};
